import { faChevronUp } from '@fortawesome/pro-regular-svg-icons/faChevronUp';
import { faHomeAlt } from '@fortawesome/pro-regular-svg-icons/faHomeAlt';
import { publicOrigin } from '@maxpreps/network/src/origins';
import { useRouter } from 'next/router';
import React from 'react';
import styled from 'styled-components';
import Anchor from '../../corecomponents/Anchor/Anchor';
import useHasMounted from '../../hooks/useHasMounted';
import useIsAdmin from '../../hooks/useIsAdmin';
import { useIsDesktop } from '../../hooks/useIsScreenSize';
import { MediaQuery } from '../../styles/breakpoint';
import THEME from '../../styles/colors';
import Icon from '../../ui/Icon/Icon';
import OverflowMenu, {
  StyledOverflowMenuWrapper
} from '../../ui/OverflowMenu/OverflowMenu';

const StyledBreadcrumbWrapper = styled.div`
  height: 40px;
  display: flex;
  align-items: stretch;
  justify-content: center;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;

  ${StyledOverflowMenuWrapper} {
    width: 100%;
  }
`;

const StyledBreadcrumbList = styled.ul`
  margin: 8px auto 0 auto;
  display: flex;
  align-items: stretch;
  flex-flow: row nowrap;
  overflow: auto;

  width: 100%;
  ${MediaQuery.Desktop} {
    // main col + right rail + margin
    max-width: ${768 + 375 + 12}px;
  }

  z-index: 1;
  position: relative;

  & > li {
    display: flex;
    align-items: center;
  }
  & > li::before {
    content: '/';
    color: ${THEME.text.secondary};
  }
  & > li:first-child::before {
    content: '';
  }
  & a,
  & span {
    padding: 0 8px;
    color: ${THEME.text.secondary};
    white-space: nowrap;
    display: flex;
    align-items: center;
  }
  & svg {
    width: 20px;
    display: block;
  }
`;

const StyledBreadcrumbDropdownHeading = styled.div`
  margin: 16px 0 8px;
  color: ${THEME.text.secondary};
  .dropdown-item {
    margin-left: 8px;
  }
  .mobile-dropdown-container:hover {
    color: ${THEME.other.link};
    svg,
    path {
      fill: ${THEME.other.link};
    }
  }
`;

export const getHomeBreadcrumbData = () => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      name: 'MaxPreps.com',
      item: { '@type': 'Thing', '@id': publicOrigin },
      position: 1
    }
  ]
});

const getAnchor = (item, attributes: any = {}) => {
  attributes.href = item.item['@id'];

  if (item.name.toLowerCase() === 'maxpreps.com' || item.position === 1) {
    return (
      <Anchor
        className="home"
        aria-label="MaxPreps Home Page"
        $hoverColor={THEME.other.link}
        {...attributes}
      >
        <Icon icon={faHomeAlt} />
      </Anchor>
    );
  }

  return (
    <Anchor
      $color={THEME.text.secondary}
      $hoverColor={THEME.other.link}
      {...attributes}
    >
      {item.name}
    </Anchor>
  );
};

interface IBreadcrumbData {
  '@type': string;
  item: {
    '@id': string;
    '@type': string;
  };
  name: string;
  position: number;
}

export interface IBreadcrumbListData {
  itemListElement: IBreadcrumbData[];
}

export interface IBreadcrumbConfig {
  data: IBreadcrumbListData;
  pageName?: string;
}

const BreadcrumbList: React.FC<IBreadcrumbConfig> = ({ data, pageName }) => {
  const router = useRouter();
  const isMobile = !useIsDesktop();
  const hasMounted = useHasMounted();
  const isAdmin = useIsAdmin();

  if (isAdmin || !Array.isArray(data?.itemListElement)) {
    return null;
  }

  let scriptData = data;
  if (pageName) {
    let url = router.asPath;
    if (!url.startsWith('https://')) {
      url = `${publicOrigin}${url}`;
    }

    const dataCopy = { ...data };
    const itemListCopy = [...data.itemListElement];
    itemListCopy.push({
      '@type': 'ListItem',
      item: {
        '@id': url,
        '@type': 'Thing'
      },
      name: pageName,
      position: data?.itemListElement.length + 1
    });
    dataCopy.itemListElement = itemListCopy;
    scriptData = dataCopy;
  }

  const menuItems = scriptData.itemListElement;

  return (
    <>
      <script
        type="application/ld+json"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(scriptData)
        }}
      />
      <StyledBreadcrumbWrapper>
        {hasMounted && isMobile ? (
          // Mobile Breadcrumb dropdown, set after initial load.
          // Note: $left + $margin produces a centering effect
          <OverflowMenu
            isIntrinsiclyPositioned={false}
            // @ts-ignore
            $left="0"
            $margin="auto"
            $maxWidth="512px"
            menuItems={menuItems
              .filter(
                item =>
                  item.position !== 1 && item.position !== menuItems.length
              )
              .map(item => ({
                text: item.name,
                as: 'a',
                asOptions: {
                  href: item.item['@id']
                }
              }))}
          >
            <StyledBreadcrumbDropdownHeading>
              {/* Home Page */}
              {menuItems &&
                getAnchor(menuItems[0], {
                  $color: THEME.text.secondary,
                  $hoverColor: THEME.other.link,
                  onClick: e => {
                    // prevent the overflow from opening
                    // take the user to this links apge
                    e.stopPropagation();
                  }
                })}
              {/* / Current Page */}
              <span className="dropdown-item">/</span>
              <span className="mobile-dropdown-container">
                <span className="dropdown-item">
                  {menuItems[menuItems.length - 1].name}
                </span>
                {menuItems.length > 2 && (
                  <Icon
                    icon={faChevronUp}
                    color={THEME.text.secondary}
                    size={12}
                    className="dropdown-item"
                    rotation={180}
                  />
                )}
              </span>
            </StyledBreadcrumbDropdownHeading>
          </OverflowMenu>
        ) : (
          // Desktop scrollable list.
          <StyledBreadcrumbList>
            {menuItems.map(item => (
              <li key={item.position}>{getAnchor(item)}</li>
            ))}
          </StyledBreadcrumbList>
        )}
      </StyledBreadcrumbWrapper>
    </>
  );
};

export default BreadcrumbList;
